/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable import/prefer-default-export */
import {useState, useCallback, useRef} from 'react';
import {DirectUpload, Blob} from '@rails/activestorage';

type XhrHandlerType = (xhr: XMLHttpRequest) => void;
type UploadStatusType = 'complete' | 'inProcess' | 'error' | 'uninitialized';
interface UseDirectUpload {
  setXhrHandler: (handler: XhrHandlerType) => void;
  uploadProgress: number;
  uploadFile: (file: File, url: string) => void;
  blob: Blob | undefined;
  error: Error | undefined;
  uploadStatus: UploadStatusType;
  resetUpload: () => void;
}

export const useDirectUpload = (): UseDirectUpload => {
  const [uploadStatus, setUploadStatus] =
    useState<UploadStatusType>('uninitialized');
  const [blob, setBlob] = useState<Blob | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadCanceler, setUploadCanceler] = useState({canceler: () => {}}); // calling setState with a function causes that function to be run
  const ref = useRef<{xhrHandler: XhrHandlerType}>({
    xhrHandler: () => {}
  });
  const setXhrHandler = useCallback((handler: XhrHandlerType) => {
    ref.current = {...ref.current, xhrHandler: handler};
  }, []);

  const resetUpload = useCallback(() => {
    if (uploadStatus === 'inProcess') uploadCanceler.canceler();
    setUploadStatus('uninitialized');
    setUploadProgress(0);
    setBlob(undefined);
    setError(undefined);
    setXhrHandler(() => {});
    setUploadCanceler({canceler: () => {}});
  }, [uploadStatus, setBlob, setError]);

  const uploadFile = useCallback(
    (file: File, url: string) => {
      const upload = new DirectUpload(file, url, {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.upload.addEventListener('progress', e => {
            setUploadProgress(Math.round((e.loaded / e.total) * 100) / 100);
          });
          ref.current.xhrHandler(xhr);
          setUploadCanceler({canceler: xhr.abort});
        }
      });

      setUploadStatus('inProcess');
      upload.create((err, blb) => {
        setUploadCanceler({canceler: () => {}});
        if (err) {
          setUploadStatus('error');
          setError(err);
        } else {
          setUploadStatus('complete');
          setBlob(blb);
        }
      });
    },
    [setUploadStatus, ref.current.xhrHandler]
  );

  return {
    setXhrHandler,
    uploadProgress,
    uploadFile,
    blob,
    error,
    uploadStatus,
    resetUpload
  };
};
